import { getSupabaseClient } from './Supa';
import { currentUser$ } from './Firebase';
import { Tables } from './database.types';

// --- Relationship Levels ---

export async function getRelationshipLevel(
  characterId: string
): Promise<Tables<'relationship_levels'> | null> {
  const user = currentUser$.get();
  if (!user) return null;

  const client = getSupabaseClient();
  const { data, error } = await client
    .from('relationship_levels')
    .select('*')
    .eq('user_id', user.uid)
    .eq('character_id', characterId)
    .maybeSingle();

  if (error) {
    console.error('[subscriptionHelpers] getRelationshipLevel error:', error);
    return null;
  }
  return data;
}

export async function upsertRelationshipLevel(
  characterId: string,
  level: number,
  affectionScore: number
): Promise<Tables<'relationship_levels'> | null> {
  const user = currentUser$.get();
  if (!user) return null;

  const client = getSupabaseClient();
  const { data, error } = await client
    .from('relationship_levels')
    .upsert({
      user_id: user.uid,
      character_id: characterId,
      level,
      affection_score: affectionScore,
    })
    .select()
    .single();

  if (error) {
    console.error('[subscriptionHelpers] upsertRelationshipLevel error:', error);
    return null;
  }
  return data;
}

// --- Custom Characters ---

export async function getCustomCharacters(): Promise<Tables<'custom_characters'>[]> {
  const user = currentUser$.get();
  if (!user) return [];

  const client = getSupabaseClient();
  const { data, error } = await client
    .from('custom_characters')
    .select('*')
    .eq('user_id', user.uid)
    .order('created_at', { ascending: false });

  if (error) {
    console.error('[subscriptionHelpers] getCustomCharacters error:', error);
    return [];
  }
  return data || [];
}

export async function createCustomCharacter(
  character: Omit<Tables<'custom_characters'>, 'id' | 'user_id' | 'created_at' | 'updated_at'>
): Promise<Tables<'custom_characters'> | null> {
  const user = currentUser$.get();
  if (!user) return null;

  const client = getSupabaseClient();
  const { data, error } = await client
    .from('custom_characters')
    .insert({ ...character, user_id: user.uid })
    .select()
    .single();

  if (error) {
    console.error('[subscriptionHelpers] createCustomCharacter error:', error);
    return null;
  }
  return data;
}

export async function deleteCustomCharacter(characterId: string): Promise<boolean> {
  const user = currentUser$.get();
  if (!user) return false;

  const client = getSupabaseClient();
  const { error } = await client
    .from('custom_characters')
    .delete()
    .eq('id', characterId)
    .eq('user_id', user.uid);

  if (error) {
    console.error('[subscriptionHelpers] deleteCustomCharacter error:', error);
    return false;
  }
  return true;
}

// --- Gift Types ---

export async function getGiftTypes(): Promise<Tables<'gift_types'>[]> {
  const client = getSupabaseClient();
  const { data, error } = await client
    .from('gift_types')
    .select('*')
    .order('coin_cost', { ascending: true });

  if (error) {
    console.error('[subscriptionHelpers] getGiftTypes error:', error);
    return [];
  }
  return data || [];
}

export async function sendGift(
  characterId: string,
  giftTypeId: string
): Promise<Tables<'gifts_sent'> | null> {
  const user = currentUser$.get();
  if (!user) return null;

  const client = getSupabaseClient();
  const { data, error } = await client
    .from('gifts_sent')
    .insert({
      user_id: user.uid,
      character_id: characterId,
      gift_type_id: giftTypeId,
    })
    .select()
    .single();

  if (error) {
    console.error('[subscriptionHelpers] sendGift error:', error);
    return null;
  }
  return data;
}

// --- Gallery Limits ---

export async function getGalleryImageCount(): Promise<number> {
  const user = currentUser$.get();
  if (!user) return 0;

  const client = getSupabaseClient();

  const { data: sessions, error: sessionError } = await client
    .from('chat_sessions')
    .select('id')
    .eq('user_id', user.uid);

  if (sessionError || !sessions?.length) return 0;

  const sessionIds = sessions.map((s: any) => s.id);

  const { count, error } = await client
    .from('messages')
    .select('*', { count: 'exact', head: true })
    .in('session_id', sessionIds)
    .not('image_url', 'is', null);

  if (error) {
    console.error('[subscriptionHelpers] getGalleryImageCount error:', error);
    return 0;
  }
  return count || 0;
}
